import { fillPath } from "@Helpers/urlPath";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * The shared folder's own top level, in a folder URL. The share never exposes the root folder's id
 * (the token is the only identity a recipient holds), so the root gets a name instead of an id.
 */
export const SHARED_ROOT_FOLDER = "root";

/** `/share/:token` — the link's entry: its landing card, and the password gate that re-unlocks it. */
export function sharedLinkPath(token: string): string {
  return fillPath("/share/:token", { token });
}

/**
 * `/share/:token/file/:fileId` — the recipient's viewer. `fileId` is a file VERSION id, under the
 * name DesignViewer reads from the route (see routes.tsx).
 */
export function sharedFilePath(token: string, fileVersionId: string): string {
  return fillPath("/share/:token/file/:fileId", { token, fileId: fileVersionId });
}

/**
 * `/share/:token/folder/:folderId` — one address per folder inside a shared folder, the folder twin
 * of `/share/:token/file/:fileId`. Recipients can bookmark or send a subfolder, and browser
 * Back/Forward walk the folders they opened. `folderId` omitted = the shared root.
 */
export function sharedFolderPath(token: string, folderId?: string): string {
  return fillPath("/share/:token/folder/:folderId", { token, folderId: folderId ?? SHARED_ROOT_FOLDER });
}

/**
 * A folder URL segment (or the older `?folder=` marker) read back: `{ folderId: undefined }` for the
 * root, `{ folderId }` for a subfolder, `null` for anything that is neither — a malformed address the
 * caller sends to the root rather than to a dead-link screen.
 */
export function parseSharedFolderParam(
  raw: string | null | undefined,
): { folderId: ObjectIdString | undefined } | null {
  if (raw === SHARED_ROOT_FOLDER) return { folderId: undefined };
  const parsed = objectIdString.safeParse(raw);
  return parsed.success ? { folderId: parsed.data } : null;
}
